import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarClock, Sparkles, Loader2 } from "lucide-react";
import { adminApi, API, tokens, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

async function adminPost(path, body) {
  const res = await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokens.admin()}` }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
}
async function adminDelete(path) {
  const res = await fetch(`${API}${path}`, { method: "DELETE", headers: { Authorization: `Bearer ${tokens.admin()}` } });
  if (!res.ok) throw new Error("Request failed");
  return res.json().catch(() => ({}));
}

export default function Schedule() {
  const [cfg, setCfg] = useState(null);
  const [cutoff, setCutoff] = useState("12:30");
  const [ev, setEv] = useState({ name: "", date_from: "", date_to: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.getSchedule().then((c) => {
    setCfg(c);
    setCutoff(`${String(c.global_cutoff_hour).padStart(2, "0")}:${String(c.global_cutoff_minute).padStart(2, "0")}`);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveWindows = async (windows) => {
    setSaving(true);
    const [h, m] = cutoff.split(":").map(Number);
    try { const c = await adminApi.saveSchedule({ global_cutoff_hour: h, global_cutoff_minute: m || 0, windows }); setCfg(c); toast.success("Schedule saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const toggleDay = (d) => {
    const has = cfg.windows.some((w) => w.delivery_day === d);
    const windows = has ? cfg.windows.filter((w) => w.delivery_day !== d) : [...cfg.windows, { delivery_day: d, cutoff_day: d }];
    saveWindows(windows);
  };
  const addEvent = async () => {
    if (!ev.name || !ev.date_from || !ev.date_to) return toast.error("Name and dates required");
    try { await adminPost("/admin/schedule/events", { ...ev, customer_id: "metcash" }); toast.success("Event added"); setEv({ name: "", date_from: "", date_to: "", notes: "" }); load(); }
    catch (err) { toast.error(err.message); }
  };
  const delEvent = async (id) => { try { await adminDelete(`/admin/schedule/events/${id}?customer_id=metcash`); load(); } catch (e) { toast.error(e.message); } };

  if (!cfg) return <p className="text-sm text-[#28282E]/55">Loading…</p>;

  return (
    <div data-testid="admin-schedule">
      <div className="mb-6"><h1 className="font-serif text-3xl font-bold text-[#28282E]">Delivery Schedule</h1><p className="text-sm text-[#28282E]/70">Configure weekly cutoffs and special events.</p></div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#28282E]" /><h2 className="font-serif text-xl font-semibold text-[#28282E]">Weekly Cutoffs</h2></div>
          <div className="mb-4 flex items-end gap-3">
            <div><Label className="text-xs">Global cutoff time (Sydney)</Label><Input data-testid="cutoff-time-input" type="time" value={cutoff} onChange={(e) => setCutoff(e.target.value)} className="mt-1 w-32" /></div>
            <Button data-testid="cutoff-save-button" onClick={() => saveWindows(cfg.windows)} disabled={saving} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save time"}</Button>
          </div>
          <Label className="text-xs">Delivery days</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const on = cfg.windows.some((w) => w.delivery_day === d);
              return <button key={d} data-testid={`day-toggle-${d}`} onClick={() => toggleDay(d)} className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold uppercase transition ${on ? "border-[#EBBAD1] bg-[#EBBAD1] text-[#28282E]" : "border-slate-200 bg-white text-[#28282E]/70"}`}>{d}</button>;
            })}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#28282E]" /><h2 className="font-serif text-xl font-semibold text-[#28282E]">Special Events</h2></div>
          <div className="space-y-2">
            {(cfg.events || []).map((e) => (
              <div key={e.id} data-testid={`event-row-${e.id}`} className="flex items-center gap-3 rounded-xl border border-[#EBBAD1] bg-[#FBEEF4] p-3">
                <div className="flex-1"><div className="text-sm font-medium text-[#28282E]">{e.name} {e.is_active ? <span className="ml-1 rounded-full bg-emerald-100 px-1.5 text-[10px] font-semibold uppercase text-emerald-700">active</span> : null}</div><div className="text-xs text-[#28282E]/70">{e.date_from} → {e.date_to}</div></div>
                <button data-testid={`event-delete-${e.id}`} onClick={() => delEvent(e.id)} className="text-[#28282E]/55 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {(cfg.events || []).length === 0 && <p className="text-sm text-[#28282E]/55">No special events yet.</p>}
          </div>
          <div className="mt-5 space-y-3 border-t border-slate-100 pt-4">
            <div><Label className="text-xs">Event name</Label><Input data-testid="event-name-input" value={ev.name} onChange={(e) => setEv({ ...ev, name: e.target.value })} className="mt-1" placeholder="Mother's Day" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">From</Label><Input data-testid="event-from-input" type="date" value={ev.date_from} onChange={(e) => setEv({ ...ev, date_from: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">To</Label><Input data-testid="event-to-input" type="date" value={ev.date_to} onChange={(e) => setEv({ ...ev, date_to: e.target.value })} className="mt-1" /></div>
            </div>
            <Button data-testid="event-add-button" onClick={addEvent} className="w-full bg-[#EBBAD1] text-[#28282E] hover:bg-[#EBBAD1]"><Plus className="mr-1.5 h-4 w-4" /> Add Event</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
